import { isEqual } from "lodash";
import {
  DataDrivenPropertyValueSpecification,
  LayerSpecification,
  SpriteSpecification,
} from "maplibre-gl";
import { StyleSpecification } from "react-map-gl/maplibre";

import { GeometryType, SchematisationVectorMeta, VectorMeta } from "@nens/rana/api-v1";
import { Extent } from "@nens/shared-utils";

import { VECTOR_LAYER_ID } from "@/config/map-layer-constants";
import { ViewTypesMap } from "@/types/descriptor-types";

// When adding multiple vector visualization to a map, each descriptor with custom styling has its own sprite.
// This means every sprite needs its own id and url (LOOK_HERE_1).
// The id is then prefixed to all sprite references in the style layers (LOOK_HERE_2).
// Documentation can be found here: https://maplibre.org/maplibre-style-spec/sprite/#multiple-sprite-sources

const getSpriteId = ({ descriptorId, styleId }: { descriptorId: string; styleId: string | null }) =>
  `sprite-id-descriptor_${descriptorId}${styleId ? `-style_${styleId}` : ""}`;

export type SpriteSpec = Exclude<SpriteSpecification, string>[number];

// LOOK_HERE_1
export const getVectorDescriptorSprite = ({
  tenant,
  descriptorId,
  styleId,
  styleJson,
}: {
  tenant: string;
  descriptorId: string;
  styleId: string | null;
  styleJson: StyleSpecification;
}): SpriteSpec | null => {
  const patchedLayers = styleJson.layers.map((layer) =>
    patchSpriteReferences({ layer, descriptorId, styleId })
  );

  // If the styleJson does not contain any sprite references the patchedLayers will be equal to the original style layers,
  // In that case we don't want to fetch the sprite because it will not exist and return a 404.
  // Which will interfere with other sprite fetches in the same map because if one sprite fetch fails,
  // all sprites will fail and the map will not render correctly.
  if (isEqual(patchedLayers, styleJson.layers)) {
    return null;
  }

  const spriteUrl = styleId
    ? `${window.location.origin}/v1-alpha/tenants/${tenant}/styles/${styleId}/sprite`
    : `${window.location.origin}/v1-alpha/tenants/${tenant}/file-descriptors/${descriptorId}/vector-style/sprite`;
  return { id: getSpriteId({ descriptorId, styleId }), url: spriteUrl };
};

// The layer id of the layers from the style json will look something like "beheerWeir:(rule#0):1"
// The layer id from the descriptorView will look like "beheerweir" but the layer name will look like "beheerWeir",
// so we need to match the style layer to the descriptor layer by matching the style layer id without the rules to the layer name from the descriptorView.
type GetLayerIdArgs =
  | {
      descriptorId: string;
      styleId: string | null;
      layerId: string;
      layerName?: never;
      isFromStyleJsonLayer: true;
    }
  | {
      descriptorId: string;
      styleId: string | null;
      layerId?: never;
      layerName: string;
      isFromStyleJsonLayer: false;
    };
// Make sure layerId is always last because it includes the rules divided by ":" which need to be at the end of the id to be preserved for correct styling in MapLibre
const getLayerId = ({
  descriptorId,
  styleId,
  layerName,
  layerId,
  isFromStyleJsonLayer,
}: GetLayerIdArgs) =>
  `layer-id-descriptor_${descriptorId}-${styleId ? `style_${styleId}-` : ""}${
    isFromStyleJsonLayer ? layerId : layerName
  }`;

// Add all the sprite references in the style layers a prefix so they are unique for each descriptor and style combination.
// This function is seperate because we use it in getVectorDescriptorSprite to see if a layer contains any sprite references
// and in getStyleLayers to actually patch the style layers with the prefix.
const patchSpriteReferences = ({
  layer: originalLayer,
  descriptorId,
  styleId,
}: {
  layer: LayerSpecification;
  descriptorId: string;
  styleId: string | null;
}) => {
  const layer = structuredClone(originalLayer);
  const prefixIfNeeded = (value: DataDrivenPropertyValueSpecification<string> | undefined) => {
    if (typeof value !== "string") return value;
    // The replace is needed to get the correct sprite name from the style. It removes any existing ":"
    return `${getSpriteId({ descriptorId, styleId })}:${value.replace(/^.*?:/, "")}`;
  };

  // Only some layer types need to be gotten from the sprite so only adjust those to add the sprite prefix.
  if (layer.layout) {
    if ("icon-image" in layer.layout) {
      layer.layout["icon-image"] = prefixIfNeeded(layer.layout["icon-image"]);
    }
  }
  if (layer.paint) {
    if ("fill-pattern" in layer.paint) {
      layer.paint["fill-pattern"] = prefixIfNeeded(layer.paint["fill-pattern"]);
    }
    if ("line-pattern" in layer.paint) {
      layer.paint["line-pattern"] = prefixIfNeeded(layer.paint["line-pattern"]);
    }
  }

  return layer;
};

// LOOK_HERE_2
const getStyleLayers = (
  styleJson: StyleSpecification | undefined,
  descriptorId: string,
  styleId: string | null
): StyleSpecification["layers"] | undefined => {
  // To disambiguate between different files on the map, we add prefixes to ids.
  // With Publications v2, we sometimes do not have a descriptorId but a styleId.
  if (!styleJson) return undefined;

  return styleJson.layers.map((originalLayer) => {
    // patchSpriteReferences clones internally; mutate the clone here for id/source-layer
    const layer = patchSpriteReferences({ layer: originalLayer, descriptorId, styleId });

    layer.id = getLayerId({
      descriptorId,
      styleId,
      layerId: originalLayer.id,
      isFromStyleJsonLayer: true,
    });

    if ("source-layer" in layer) {
      layer["source-layer"] = VECTOR_LAYER_ID;
    }

    return layer;
  });
};

// Filters style layers by layer name. The layer ids are already prefixed with the descriptorId
// (done in getStyleLayers), so the filter matches against the prefixed name.
const filterStyleLayers = ({
  styleLayers,
  layerName,
  descriptorId,
  styleId,
}: {
  styleLayers: StyleSpecification["layers"];
  layerName: string;
  descriptorId: string;
  styleId: string | null;
}): StyleSpecification["layers"] =>
  styleLayers.filter((layer) => {
    const styleLayerIdWithoutRules = layer.id.split(":").slice(0, -2).join(":"); // Example without the stuff added in getLayerId "beheerstedelijkwater:beheerpomp:(rule#0):1"
    return (
      styleLayerIdWithoutRules ===
      getLayerId({ descriptorId, styleId, layerName, isFromStyleJsonLayer: false })
    );
  });

type GetVectorDescriptorLayersProps = {
  descriptorId: string;
  meta: VectorMeta | SchematisationVectorMeta;
  styleJson: StyleSpecification | undefined;
  descriptorView: ViewTypesMap["pmtiles"];
  styleId: string | null;
};

export type VectorDescriptorLayer = {
  styleLayers: StyleSpecification["layers"] | undefined;
  descriptorId: string;
  layer: string;
  type: GeometryType | undefined;
  url: string;
  bounds: Extent | null;
  isInverse: boolean;
};

export const getVectorDescriptorLayers = ({
  descriptorId,
  meta,
  styleJson,
  descriptorView,
  styleId,
}: GetVectorDescriptorLayersProps): VectorDescriptorLayer[] => {
  const styleLayers = getStyleLayers(styleJson, descriptorId, styleId);
  const urls = descriptorView.urls;
  const bounds = meta.envelope as Extent | null; // FUTURE - remove as when backend typing is more strict

  return meta.layers.map(({ id, name, type }) => {
    return {
      styleLayers: styleLayers
        ? filterStyleLayers({
            styleLayers,
            layerName: name,
            descriptorId,
            styleId,
          })
        : undefined,
      descriptorId,
      // Layer can be any string as long as its unique
      layer: id,
      type,
      url: urls[id],
      bounds,
      isInverse: meta.featureTypeDefinition === "study_area",
    };
  });
};
